/* Link.js */

"use strict";

const _ = require("lodash");
const EventEmitter = require("events").EventEmitter;
const uiCore = require("@gluenetworks/steve_ui_core");
const {
    Control: {
        Boolean: {InputCheckbox},
    },
} = uiCore;

const NETBOX_DEVICE = {
    // NetBox Device property name equivalent to...
    NAME: "name", // Gluware name property
    SERIAL: "serial", // Gluware serialNumber property
    HOSTNAME: "name", // Gluware hostName property
};

class Link extends EventEmitter {
    constructor(dataSyncConfigEntity) {
        super();

        this._nbDataSyncConfigEntity = dataSyncConfigEntity;
        this._tabMainContent = undefined;
        this._matchSerialNumberCheckbox = undefined;
        this._matchDeviceNameCheckbox = undefined;
        this._matchDeviceHostnameCheckbox = undefined;
        this._createNetBoxDevicesCheckbox = undefined;
        this._createGluwareDevicesCheckbox = undefined;

        this._noConnectionEl = this._createNoConnectionPlaceholder();

        this._onValueChanged = this._onValueChanged.bind(this);
    }

    /*******************************************/
    /* START PRIVATE METHODS                   */
    /*******************************************/

    _getLinkConfig() {
        const deviceLinking = {};

        if (this._matchSerialNumberCheckbox.getValue()) {
            deviceLinking.detectedSerialNumber = NETBOX_DEVICE.SERIAL;
        }

        if (this._matchDeviceNameCheckbox.getValue()) {
            deviceLinking.name = NETBOX_DEVICE.NAME;
        }

        if (this._matchDeviceHostnameCheckbox.getValue()) {
            deviceLinking.detectedHostName = NETBOX_DEVICE.HOSTNAME;
        }

        let createDeviceVal = !!this._createGluwareDevicesCheckbox.getValue();
        if (
            !createDeviceVal &&
            _.isUndefined(this._nbDataSyncConfigEntity.pristineProperties.createDevices)
        ) {
            createDeviceVal = undefined;
        }

        let createCisVal = !!this._createNetBoxDevicesCheckbox.getValue();
        if (
            !createCisVal &&
            _.isUndefined(this._nbDataSyncConfigEntity.pristineProperties.createCis)
        ) {
            createCisVal = undefined;
        }

        return {
            instanceLinkingCriteria: {
                deviceLinking,
            },
            createCis: createCisVal,
            createDevices: createDeviceVal,
        };
    }

    _setLinkConfig(linkConfig, setByUser = false) {
        if (_.isEmpty(linkConfig)) {
            return;
        }

        const {deviceLinking = {}} = _.get(linkConfig, "instanceLinkingCriteria", {});

        this._matchSerialNumberCheckbox.setValue(
            deviceLinking.detectedSerialNumber === NETBOX_DEVICE.SERIAL,
            setByUser
        );
        this._matchDeviceNameCheckbox.setValue(
            deviceLinking.name === NETBOX_DEVICE.NAME,
            setByUser
        );
        this._matchDeviceHostnameCheckbox.setValue(
            deviceLinking.detectedHostName === NETBOX_DEVICE.HOSTNAME,
            setByUser
        );
        this._createNetBoxDevicesCheckbox.setValue(!!linkConfig.createCis, setByUser);
        this._createGluwareDevicesCheckbox.setValue(!!linkConfig.createDevices, setByUser);
    }

    _resetLinkConfig() {
        this._setLinkConfig(this._nbDataSyncConfigEntity);
    }

    _createSection(content, options = {}) {
        const section = document.createElement("section");
        section.className = options.className || "";
        section.classList.toggle("bordered", !!options.bordered);

        if (options.title) {
            const title = document.createElement("h3");
            title.innerText = options.title;
            section.appendChild(title);
        }
        section.appendChild(content);
        return section;
    }

    _registerListeners() {
        this._matchSerialNumberCheckbox.on("UserValue", this._onValueChanged);
        this._matchDeviceNameCheckbox.on("UserValue", this._onValueChanged);
        this._matchDeviceHostnameCheckbox.on("UserValue", this._onValueChanged);
        this._createNetBoxDevicesCheckbox.on("UserValue", this._onValueChanged);
        this._createGluwareDevicesCheckbox.on("UserValue", this._onValueChanged);
    }

    _unRegisterListeners() {
        this._matchSerialNumberCheckbox.removeListener("UserValue", this._onValueChanged);
        this._matchDeviceNameCheckbox.removeListener("UserValue", this._onValueChanged);
        this._matchDeviceHostnameCheckbox.removeListener("UserValue", this._onValueChanged);
        this._createNetBoxDevicesCheckbox.removeListener("UserValue", this._onValueChanged);
        this._createGluwareDevicesCheckbox.removeListener("UserValue", this._onValueChanged);
    }

    _createNoConnectionPlaceholder() {
        return this._createBanner(
            "Unable to connect to NetBox. Please verify configured connection details.",
            "no-cmdb-connection"
        );
    }

    _createBanner(msg, className) {
        const msgContainer = document.createElement("div");
        msgContainer.classList.add(className);
        const innerSpan = document.createElement("span");
        innerSpan.innerText = msg;

        msgContainer.appendChild(innerSpan);

        return msgContainer;
    }

    _connectionFailed() {
        this._noConnectionEl.classList.add("show");
        this._tabMainContent.classList.add("no-connection");
    }

    _connectionPassed() {
        this._noConnectionEl.classList.remove("show");
        this._tabMainContent.classList.remove("no-connection");
        this._resetLinkConfig();
    }

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onValueChanged() {
        const linkConfig = this._getLinkConfig();
        this._nbDataSyncConfigEntity.setInstanceLinkingCriteria(
            linkConfig.instanceLinkingCriteria || {}
        );
        this._nbDataSyncConfigEntity.setCreateCis(linkConfig.createCis);
        this._nbDataSyncConfigEntity.setCreateDevices(linkConfig.createDevices);
        this.emit("ConfigChanged", linkConfig);
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PUBLIC METHODS                    */
    /*******************************************/

    createLinkTab() {
        this._tabMainContent = document.createElement("div");
        this._tabMainContent.classList.add("content-main");

        this._tabMainContent.appendChild(this._noConnectionEl);

        this._matchSerialNumberCheckbox = new InputCheckbox({
            label: "Gluware Device Serial Number ↔ NetBox Device Serial Number",
            labelShown: true,
            value: false, // Disabled By Default
            darkTheme: true,
        });
        this._matchDeviceNameCheckbox = new InputCheckbox({
            label: "Gluware Device Name ↔ NetBox Device Name",
            labelShown: true,
            value: false, // Disabled By Default
            darkTheme: true,
        });
        this._matchDeviceHostnameCheckbox = new InputCheckbox({
            label: "Gluware Device Hostname ↔ NetBox Device Name",
            labelShown: true,
            value: false, // Disabled By Default
            darkTheme: true,
        });
        this._createNetBoxDevicesCheckbox = new InputCheckbox({
            label: "Create a NetBox device if a Gluware device cannot be linked to an existing NetBox device",
            labelShown: true,
            value: false, // Disabled By Default
            darkTheme: true,
        });
        this._createGluwareDevicesCheckbox = new InputCheckbox({
            label: "Create a Gluware device if a NetBox device cannot be linked to an existing Gluware device",
            labelShown: true,
            value: false, // Disabled By Default
            darkTheme: true,
        });

        const matchingCriteriaFragment = document.createDocumentFragment();
        matchingCriteriaFragment.appendChild(this._matchSerialNumberCheckbox._renderer._rootElem);
        matchingCriteriaFragment.appendChild(this._matchDeviceNameCheckbox._renderer._rootElem);
        matchingCriteriaFragment.appendChild(this._matchDeviceHostnameCheckbox._renderer._rootElem);

        const matchingCriteriaSection = this._createSection(matchingCriteriaFragment, {
            title: "Matching Criteria",
            bordered: true,
        });

        const unmatchedHandlingFragment = document.createDocumentFragment();
        unmatchedHandlingFragment.appendChild(
            this._createNetBoxDevicesCheckbox._renderer._rootElem
        );
        unmatchedHandlingFragment.appendChild(
            this._createGluwareDevicesCheckbox._renderer._rootElem
        );

        const unmatchedHandlingSection = this._createSection(unmatchedHandlingFragment, {
            title: "Unmatched Handling",
            bordered: true,
        });

        this._setLinkConfig(this._nbDataSyncConfigEntity);
        this._registerListeners();

        this._tabMainContent.appendChild(matchingCriteriaSection);
        this._tabMainContent.appendChild(unmatchedHandlingSection);
    }

    getLinkTabContent() {
        return this._tabMainContent;
    }

    validateLinkTab() {
        const linkConfig = this._getLinkConfig();
        return !(
            _.get(linkConfig, "instanceLinkingCriteria.deviceLinking.name") !==
                NETBOX_DEVICE.NAME &&
            _.get(linkConfig, "instanceLinkingCriteria.deviceLinking.detectedSerialNumber") !==
                NETBOX_DEVICE.SERIAL &&
            _.get(linkConfig, "instanceLinkingCriteria.deviceLinking.detectedHostName") !==
                NETBOX_DEVICE.HOSTNAME
        );
    }

    shutdown() {
        this._unRegisterListeners();

        this._noConnectionEl = undefined;

        const components = [
            this._tabMainContent,
            this._matchSerialNumberCheckbox,
            this._matchDeviceNameCheckbox,
            this._matchDeviceHostnameCheckbox,
            this._createNetBoxDevicesCheckbox,
            this._createGluwareDevicesCheckbox,
        ];

        return Promise.all(components.map((component) => component.shutdown())).then(() => {
            components.forEach((component, index) => {
                components[index] = undefined;
            });
        });
    }
    setDataSyncEntity(ety) {
        this._nbDataSyncConfigEntity = ety;
    }
    /*******************************************/
    /* END PUBLIC METHODS                      */
    /*******************************************/
}

module.exports = Link;
